import { Portal, Select as ChakraSelect, createListCollection } from "@chakra-ui/react";
import { forwardRef, memo, useMemo } from 'react';

// Select styles using design tokens
const selectRootStyles = {};

const selectControlStyles = {
  bg: "white",
  _dark: { bg: "gray.700" },
  borderColor: "gray.300",
  _hover: { borderColor: "gray.400" },
  _focus: { 
    borderColor: "brand.600", 
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)" 
  },
};

const selectContentStyles = {
  bg: "white",
  borderColor: "gray.200",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  zIndex: 9999, // High z-index to appear above modals
  _dark: { 
    bg: "gray.700", 
    borderColor: "gray.600" 
  },
};

const selectItemStyles = {
  _hover: { 
    bg: "brand.50", 
    _dark: { bg: "brand.900" } 
  },
  _selected: { 
    bg: "brand.100",
    color: "brand.700",
    _dark: { bg: "brand.800", color: "brand.200" } 
  },
};

const selectLabelStyles = {
  fontSize: "sm",
  fontWeight: "medium",
  color: "gray.700",
  _dark: { color: "gray.300" },
  mb: 2,
};

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  placeholder?: string;
  label?: string;
  size?: "xs" | "sm" | "md" | "lg";
  width?: string;
  disabled?: boolean;
  required?: boolean;
}

const Select = memo(forwardRef<HTMLDivElement, SelectProps>(
  function Select({
    options,
    value,
    onChange,
    onBlur,
    name,
    placeholder = "Select an option",
    label,
    size = "sm",
    width = "full",
    disabled = false,
    required = false,
  }, ref) {
    
    // Create collection for Chakra Select - memoize it to avoid recreation
    const collection = useMemo(() => 
      createListCollection({ items: options })
    , [options]);
    
    // Handle value change
    const handleValueChange = (details: { value: string[] }) => {
      const selectedValue = details.value[0] || "";
      onChange?.(selectedValue);
    };

    return (
      <ChakraSelect.Root
        ref={ref}
        collection={collection}
        size={size}
        width={width}
        value={value ? [value] : []}
        onValueChange={handleValueChange}
        onInteractOutside={onBlur}
        name={name}
        disabled={disabled}
        required={required}
        {...selectRootStyles}
      >
        <ChakraSelect.HiddenSelect />
        {label && (
          <ChakraSelect.Label {...selectLabelStyles}>
            {label}
          </ChakraSelect.Label>
        )}
        <ChakraSelect.Control {...selectControlStyles}>
          <ChakraSelect.Trigger>
            <ChakraSelect.ValueText placeholder={placeholder} />
          </ChakraSelect.Trigger>
          <ChakraSelect.IndicatorGroup>
            <ChakraSelect.Indicator />
          </ChakraSelect.IndicatorGroup>
        </ChakraSelect.Control>
        <Portal>
          <ChakraSelect.Positioner zIndex={9999}>
            <ChakraSelect.Content {...selectContentStyles}>
              {options.map((option) => (
                <ChakraSelect.Item 
                  item={option} 
                  key={option.value}
                  {...selectItemStyles}
                >
                  {option.label}
                  <ChakraSelect.ItemIndicator />
                </ChakraSelect.Item>
              ))}
            </ChakraSelect.Content>
          </ChakraSelect.Positioner>
        </Portal>
      </ChakraSelect.Root>
    );
  }
));

Select.displayName = 'Select';

export default Select; 